import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { FastField, Field, Form, Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import Loader from "../../../components/Loader";
import { Module, SUPPORTED_LANGUAGES } from "../../../constants";
import { getTranslatedServerMessage } from "../../../utils/translations";
import { getCurrentUrl } from "../../../utils/urls";
import {
  PUBLIC_TEST_URL,
  START_SESSION_ID_SUB_URL,
} from "../../Frontend/constants";
import { TEST_SETS_CATEGORY_ORDER, TESTS_URL } from "../constants";
import Attributes from "./Attributes";
import { CategorySheetSelection } from "./CategorySheetSelection";
import { ClientDropdown, LanguageDropdown } from "./components";
import { useClientsData } from "./hooks/useClientsData";
import { useSheetData } from "./hooks/useSheetData";
import { SingleSheetSelection } from "./SingleSheetSelection";
import {
  cutStackTrace,
  generateAttr,
  getAttributeValidation,
  getAttributeValues,
} from "./utils";

const attributeIds = [1, 2, 3, 4, 5];

function TestForm({ test, onSubmit, error, loading, duplicateMode, editMode }) {
  const history = useHistory();
  const { t } = useTranslation();
  const [tab, setTab] = React.useState("module0");
  const [selectedClientId, setSelectedClientId] = React.useState(
    test?.clientId
  );

  const clientsData = useClientsData();
  const sheetData = useSheetData();

  if (clientsData.isLoading || sheetData.isLoading) {
    return <Loader />;
  }

  if (clientsData.isError || sheetData.isError) {
    return (
      <Typography color="secondary">
        {t("tool.addEdit.failedToLoadData", "Failed to load data")}
      </Typography>
    );
  }

  const clientModules = clientsData.data?.find(
    c => c._id === selectedClientId
  )?.modules;

  const hasModule1 = clientModules?.includes(Module.Analytics);
  const hasModule2 = clientModules?.includes(Module.Reflection);
  const hasModule3 = clientModules?.includes(Module.Solution);

  const renderOtherTab = (left, right) => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ maxWidth: 600, mx: "auto" }}>
            <Link
              href="https://drive.google.com/drive/u/4/folders/1xhDPi3dWAvlhvFUP2rXXaCNmQ3W0HIdB"
              target="_blank"
              underline="hover"
              sx={{ display: "inline-block", mb: "10px" }}
            >
              {t("tool.addEdit.testSets", "Test-Sets")}
            </Link>
            {left}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={3}>
            {right}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderSheetSelect = (module, formikProps) => {
    const { values, setFieldValue, touched, errors } = formikProps;
    const moduleFieldCount = {
      module2: 3,
      module3: 1,
    };
    const fieldLabels = {
      module2: [
        t("tool.addEdit.module2.leverages", "Leverages"),
        t("tool.addEdit.module2.challenges", "Challenges"),
        t("tool.addEdit.module2.selfEvaluation", "Self-Evaluation"),
      ],
      module3: [t("tool.addEdit.module3.solutionJourney", "Solution-Journey")],
    };
    return (
      <>
        <SingleSheetSelection
          sheetData={sheetData.data}
          fieldName={`${module}SheetIds`}
          fieldCount={moduleFieldCount[module]}
          fieldLabels={fieldLabels[module]}
          sheetIds={values[`${module}SheetIds`]}
          setFieldValue={setFieldValue}
        />
        {errors?.[`${module}SheetIds`] && touched?.[`${module}SheetIds`] && (
          <Typography color="secondary" sx={{ pb: 3 }}>
            {errors[`${module}SheetIds`]}
          </Typography>
        )}
      </>
    );
  };

  const renderHtmlField = (key, props, formikProps) => {
    const { touched, errors } = formikProps;
    return (
      <FastField
        name={key}
        as={TextField}
        multiline
        rows={4}
        variant="outlined"
        fullWidth
        required
        error={touched[key] && Boolean(errors[key])}
        helperText={touched[key] && errors[key]}
        {...props}
      />
    );
  };

  const renderHtmlFields = (module, formikProps) => {
    const isModule1 = module === "module1";
    const isModule2 = module === "module2";

    const startName = isModule1 ? "startHtml" : `${module}StartHtml`;
    const endName = isModule1 ? "endHtml" : `${module}EndHtml`;

    const labels = {
      startHtml: t("tool.addEdit.startHtml", "Start HTML"),
      endHtml: t("tool.addEdit.endHtml", "End HTML"),
      module2StartHtml: t("tool.addEdit.start2Html", "Start 2 HTML"),
      module2EndHtml: t("tool.addEdit.end2Html", "End 2 HTML"),
      module3StartHtml: t("tool.addEdit.start3Html", "Start 3 HTML"),
      module3EndHtml: t("tool.addEdit.end3Html", "End 3 HTML"),
    };

    const startLabel = labels[startName];
    const endLabel = labels[endName];

    const renderField = (name, label) => {
      return renderHtmlField(name, { label, sx: { mb: 2 } }, formikProps);
    };

    return (
      <Grid item xs={12}>
        {renderField(startName, startLabel)}
        {isModule1 &&
          renderField(
            "introPersonalSummaryHtml",
            t(
              "tool.addEdit.introPersonalSummaryHtml",
              "Intro Personal Summary HTML"
            )
          )}
        {renderField(endName, endLabel)}
        {isModule1 && (
          <>
            {renderField(
              "introOrganizationHtml",
              t("tool.addEdit.introOrganizationHtml", "Intro Organization HTML")
            )}
            {renderField(
              "introTeamHtml",
              t("tool.addEdit.introTeamHtml", "Intro Team HTML")
            )}
            {renderField(
              "introLeadershipHtml",
              t("tool.addEdit.introLeadershipHtml", "Intro Leadership HTML")
            )}
          </>
        )}
        {isModule2 && (
          <>
            {renderField(
              "introLeveragesHtml",
              t("tool.addEdit.introLeveragesHtml", "Intro Leverages HTML")
            )}
            {renderField(
              "introChallengesHtml",
              t("tool.addEdit.introChallengesHtml", "Intro Challenges HTML")
            )}
          </>
        )}
      </Grid>
    );
  };

  return (
    <Formik
      initialValues={{
        name: "",
        languageId: SUPPORTED_LANGUAGES[0].id,
        startHtml: "",
        endHtml: "",
        module2StartHtml: "",
        module2EndHtml: "",
        module3StartHtml: "",
        module3EndHtml: "",
        startReportHtml: "",
        attributeSelectionHtml: "",
        introOverallHtml: "",
        introInstructionsHtml: "",
        introPersonalSummaryHtml: "",
        introOrganizationHtml: "",
        introTeamHtml: "",
        introLeadershipHtml: "",
        introLeveragesHtml: "",
        introChallengesHtml: "",
        maxReplies: 0,
        isActive: true,
        module2SheetIds: [],
        module3SheetIds: [],
        sheetIds: [],
        sheetsByCategories: {},
        ...getAttributeValues(attributeIds, "attribute"),
        ...getAttributeValues(attributeIds, "specialAttribute"),
        ...(test &&
          Object.keys(test).reduce(
            (acc, key) => ({
              ...acc,
              [key]: (() => {
                if (key.includes("Value")) {
                  return generateAttr(test[key], { compose: true });
                }
                return test[key];
              })(),
            }),
            {}
          )),
      }}
      validationSchema={Yup.object().shape(
        {
          name: Yup.string().required(
            t("tool.addEdit.nameRequired", "Name is required")
          ),
          startReportHtml: Yup.string().required(
            t(
              "tool.addEdit.startReportHtmlRequired",
              "Start Report HTML is required"
            )
          ),
          introOverallHtml: Yup.string().required(
            t(
              "tool.addEdit.introOverallHtmlRequired",
              "Intro Overall HTML is required"
            )
          ),
          introInstructionsHtml: Yup.string().required(
            t(
              "tool.addEdit.introInstructionsHtmlRequired",
              "Intro Instructions HTML is required"
            )
          ),
          attributeSelectionHtml: Yup.string().required(
            t(
              "tool.addEdit.attributeSelectionHtmlRequired",
              "Attribute Selection HTML is required"
            )
          ),
          ...(hasModule1 && {
            sheetsByCategories: Yup.object().test(
              "at-least-one-category",
              t("tool.addEdit.minOneSetRequired", "Minimum of 1 set"),
              value => {
                if (!value) return false;
                return TEST_SETS_CATEGORY_ORDER.some(
                  category => value[category]?.filter(Boolean)?.length > 0
                );
              }
            ),
            startHtml: Yup.string().required(
              t("tool.addEdit.startHtmlRequired", "Start HTML is required")
            ),
            endHtml: Yup.string().required(
              t("tool.addEdit.endHtmlRequired", "End HTML is required")
            ),
            introPersonalSummaryHtml: Yup.string().required(
              t(
                "tool.addEdit.introPersonalSummaryHtmlRequired",
                "Intro Personal Summary HTML is required"
              )
            ),
            introOrganizationHtml: Yup.string().required(
              t(
                "tool.addEdit.introOrganizationHtmlRequired",
                "Intro Organization HTML is required"
              )
            ),
            introTeamHtml: Yup.string().required(
              t(
                "tool.addEdit.introTeamHtmlRequired",
                "Intro Team HTML is required"
              )
            ),
            introLeadershipHtml: Yup.string().required(
              t(
                "tool.addEdit.introLeadershipHtmlRequired",
                "Intro Leadership HTML is required"
              )
            ),
          }),
          ...(hasModule2 && {
            module2SheetIds: Yup.array().test(
              "at-least-one-sheet",
              t(
                "tool.addEdit.minOneSheetRequired",
                "At least 1 sheet is required"
              ),
              value => {
                return value && value.filter(Boolean).length > 0;
              }
            ),
            module2StartHtml: Yup.string().required(
              t(
                "tool.addEdit.module2StartHtmlRequired",
                "Module 2 Start HTML is required"
              )
            ),
            module2EndHtml: Yup.string().required(
              t(
                "tool.addEdit.module2EndHtmlRequired",
                "Module 2 End HTML is required"
              )
            ),
            introLeveragesHtml: Yup.string().required(
              t(
                "tool.addEdit.introLeveragesHtmlRequired",
                "Intro Leverages HTML is required"
              )
            ),
            introChallengesHtml: Yup.string().required(
              t(
                "tool.addEdit.introChallengesHtmlRequired",
                "Intro Challenges HTML is required"
              )
            ),
          }),
          ...(hasModule3 && {
            module3SheetIds: Yup.array().test(
              "at-least-one-sheet",
              t(
                "tool.addEdit.minOneSheetRequired",
                "At least 1 sheet is required"
              ),
              value => {
                return value && value.filter(Boolean).length > 0;
              }
            ),
            module3StartHtml: Yup.string().required(
              t(
                "tool.addEdit.module3StartHtmlRequired",
                "Module 3 Start HTML is required"
              )
            ),
            module3EndHtml: Yup.string().required(
              t(
                "tool.addEdit.module3EndHtmlRequired",
                "Module 3 End HTML is required"
              )
            ),
          }),
          ...getAttributeValidation(attributeIds, "attribute", t),
          ...getAttributeValidation(attributeIds, "specialAttribute", t),
        },
        [
          ...attributeIds.map(id => [
            `attribute${id}Label`,
            `attribute${id}Value`,
          ]),
          ...attributeIds.map(id => [
            `specialAttribute${id}Label`,
            `specialAttribute${id}Value`,
          ]),
        ]
      )}
      onSubmit={async values => {
        const attrValues = Object.keys(values).reduce(
          (acc, key) =>
            key.includes("Value")
              ? {
                  ...acc,
                  [key]:
                    generateAttr(values[key])?.filter(l => l?.trim()) || "",
                }
              : acc,
          {}
        );
        Object.keys(values).forEach(key => {
          if (key.includes("_") || key === "createdAt" || key === "updatedAt") {
            delete values[key];
          }
        });

        const sheetIds = TEST_SETS_CATEGORY_ORDER.reduce((acc, category) => {
          if (values.sheetsByCategories[category]) {
            acc.push(...values.sheetsByCategories[category].filter(Boolean));
          }
          return acc;
        }, []);

        const sheetsByCategories = TEST_SETS_CATEGORY_ORDER.reduce(
          (acc, category) => {
            if (values.sheetsByCategories[category]) {
              acc[category] =
                values.sheetsByCategories[category].filter(Boolean);
            }
            return acc;
          },
          {}
        );

        const res = await onSubmit({
          ...values,
          ...attrValues,
          module2SheetIds: values.module2SheetIds?.filter(Boolean),
          module3SheetIds: values.module3SheetIds?.filter(Boolean),
          sheetIds,
          sheetsByCategories,
        });

        if (res) {
          history.push(`${TESTS_URL}/${res._id}`);
        }
      }}
    >
      {formikProps => {
        const { values, touched, errors, setFieldValue } = formikProps;

        const renderField = (name, label) => {
          return renderHtmlField(name, { label, sx: { mb: 2 } }, formikProps);
        };
        return (
          <Form>
            <TabContext value={tab}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList onChange={(_, newTab) => setTab(newTab)}>
                  <Tab
                    label={t("tool.addEdit.module0.tabTitle", "General")}
                    value="module0"
                  />
                  {hasModule1 && (
                    <Tab
                      label={t("tool.addEdit.module1.tabTitle", "Module 1")}
                      value="module1"
                    />
                  )}
                  {hasModule2 && (
                    <Tab
                      label={t("tool.addEdit.module2.tabTitle", "Module 2")}
                      value="module2"
                    />
                  )}
                  {hasModule3 && (
                    <Tab
                      label={t("tool.addEdit.module3.tabTitle", "Module 3")}
                      value="module3"
                    />
                  )}
                </TabList>
              </Box>
              <TabPanel value="module0">
                <Grid container spacing={2}>
                  <Grid item sm={6}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <ClientDropdown
                          clientList={clientsData.data}
                          onChange={setSelectedClientId}
                        />
                        <FastField
                          name="name"
                          as={TextField}
                          required
                          label={t(
                            "tool.addEdit.testName",
                            "Name / Description"
                          )}
                          fullWidth
                          variant="outlined"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          sx={{ mt: 2 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {renderField(
                          "introOverallHtml",
                          t(
                            "tool.addEdit.introOverallHtml",
                            "Intro Overall HTML"
                          )
                        )}
                        {renderField(
                          "introInstructionsHtml",
                          t(
                            "tool.addEdit.introInstructionsHtml",
                            "Intro Instructions HTML"
                          )
                        )}
                        {renderField(
                          "startReportHtml",
                          t("tool.addEdit.startReportHtml", "Report Start HTML")
                        )}
                        {renderField(
                          "attributeSelectionHtml",
                          t(
                            "tool.addEdit.attributeSelectionHtml",
                            "Attribute Selection HTML"
                          )
                        )}
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      sx={{ pt: 2 }}
                    >
                      <Grid item xs={12} sm={2} />
                      <Grid item xs={12} sm={10}>
                        <FormControlLabel
                          label={t("tool.addEdit.active", "Active")}
                          control={
                            <FastField
                              type="checkbox"
                              as={Checkbox}
                              name="isActive"
                              color="primary"
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Typography>
                          {t("tool.addEdit.maxReplies", "Survey Limit")}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={10}>
                        <Field
                          name="maxReplies"
                          as={TextField}
                          fullWidth
                          size="small"
                          type="number"
                          variant="outlined"
                          inputProps={{ min: 0 }}
                          onBlur={() => {
                            if (!values.maxReplies || values.maxReplies < 0) {
                              setFieldValue("maxReplies", 0);
                            }
                          }}
                        />
                      </Grid>
                      {test && !duplicateMode && (
                        <>
                          <Grid item xs={12} sm={2}>
                            <Typography>
                              {t("tool.addEdit.testLink", "Test Link")}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={10}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              size="small"
                              defaultValue={getCurrentUrl(
                                `${PUBLIC_TEST_URL}/${test._id}/${START_SESSION_ID_SUB_URL}/intro/overall`
                              )}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12}>
                        <LanguageDropdown />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item sm={6}>
                    <Attributes ids={attributeIds} prefix="specialAttribute" />
                    <Box height="40px" />
                    <Attributes ids={attributeIds} prefix="attribute" />
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="module1">
                {renderOtherTab(
                  <>
                    <CategorySheetSelection
                      sheetData={sheetData.data}
                      sheetsByCategories={values.sheetsByCategories}
                      sheetIds={values.sheetIds}
                      setFieldValue={setFieldValue}
                    />
                    {errors?.sheetsByCategories && touched?.sheetsByCategories && (
                      <Typography color="secondary" sx={{ mt: -3, pb: 3 }}>
                        {errors.sheetsByCategories}
                      </Typography>
                    )}
                  </>,
                  renderHtmlFields("module1", formikProps)
                )}
              </TabPanel>
              <TabPanel value="module2">
                {renderOtherTab(
                  renderSheetSelect("module2", formikProps),
                  renderHtmlFields("module2", formikProps)
                )}
              </TabPanel>
              <TabPanel value="module3">
                {renderOtherTab(
                  renderSheetSelect("module3", formikProps),
                  renderHtmlFields("module3", formikProps)
                )}
              </TabPanel>
            </TabContext>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                padding: "21px",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                onClick={() => {
                  formikProps.handleSubmit(); //make rerender

                  setTimeout(async () => {
                    //get tab with errors
                    const formErrors = await formikProps.validateForm();
                    if (!formErrors || Object.keys(formErrors).length === 0) {
                      return;
                    }

                    const fieldTabMapping = {
                      name: "module0",
                      languageId: "module0",
                      startReportHtml: "module0",
                      attributeSelectionHtml: "module0",
                      maxReplies: "module0",
                      isActive: "module0",
                      ...attributeIds.reduce(
                        (acc, id) => ({
                          ...acc,
                          [`attribute${id}Label`]: "module0",
                          [`attribute${id}Value`]: "module0",
                          [`specialAttribute${id}Label`]: "module0",
                          [`specialAttribute${id}Value`]: "module0",
                        }),
                        {}
                      ),
                      startHtml: "module1",
                      endHtml: "module1",
                      sheetsByCategories: "module1",
                      sheetIds: "module1",
                      module2StartHtml: "module2",
                      module2EndHtml: "module2",
                      module2SheetIds: "module2",
                      module3StartHtml: "module3",
                      module3EndHtml: "module3",
                      module3SheetIds: "module3",
                      introOverallHtml: "module0",
                      introInstructionsHtml: "module0",
                      introPersonalSummaryHtml: "module1",
                      introOrganizationHtml: "module1",
                      introTeamHtml: "module1",
                      introLeadershipHtml: "module1",
                      introLeveragesHtml: "module2",
                      introChallengesHtml: "module2",
                    };

                    const tabsWithErrors = Object.keys(formErrors)
                      .map(fieldName => fieldTabMapping[fieldName])
                      .filter(tab => tab);

                    const tabWithErrors = tabsWithErrors[0] || null;
                    if (tabWithErrors && tabWithErrors !== tab) {
                      setTab(tabWithErrors);
                    }
                  }, 0);
                }}
              >
                {test || editMode || duplicateMode
                  ? t("tool.addEdit.save", "Save")
                  : t("tool.addEdit.create", "Create")}
              </Button>
              <Button
                color="secondary"
                onClick={() => {
                  history.push(TESTS_URL);
                }}
              >
                {t("tool.addEdit.close", "Close")}
              </Button>
              {loading && <Loader />}
              {error && (
                <Typography
                  color="secondary"
                  title={cutStackTrace(error?.response?.data?.stack)}
                >
                  {getTranslatedServerMessage(error?.response?.data?.message) ??
                    t(
                      "tool.addEdit.unexpectedError",
                      "Unexpected error occurred"
                    )}
                </Typography>
              )}
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export default TestForm;
